import { useCallback, useRef, useState } from "react";
import { messagesToAgentChatRequest, playgroundUiMessage, type McpPlaygroundMessage } from "./chatMessages";

const CHAT_REQUEST_FAILED_MESSAGE = "Chat request failed. Please try again.";

type UseMcpPlaygroundAgentChatOptions = {
  api: string;
  fetch: typeof fetch;
};

export function useMcpPlaygroundAgentChat({ api, fetch: authedFetch }: UseMcpPlaygroundAgentChatOptions) {
  const [messages, setMessagesState] = useState<McpPlaygroundMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>();
  const isLoadingRef = useRef(false);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const setMessages = useCallback(
    (updater: McpPlaygroundMessage[] | ((current: McpPlaygroundMessage[]) => McpPlaygroundMessage[])) => {
      setMessagesState((current) => (typeof updater === "function" ? updater(current) : updater));
    },
    []
  );

  const sendMessage = useCallback(
    async (text: string): Promise<boolean> => {
      const trimmed = text.trim();
      if (!trimmed || isLoadingRef.current) {
        return false;
      }

      const userMessage = playgroundUiMessage("user", trimmed);
      const requestMessages = [...messagesRef.current, userMessage];

      setMessagesState(requestMessages);
      setIsLoading(true);
      isLoadingRef.current = true;
      setError(undefined);

      try {
        const response = await authedFetch(api, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/plain"
          },
          body: JSON.stringify({ messages: messagesToAgentChatRequest(requestMessages) })
        });

        if (!response.ok) {
          let errorMessage = CHAT_REQUEST_FAILED_MESSAGE;
          const contentType = response.headers.get("content-type") ?? "";
          if (contentType.includes("application/json")) {
            const body = (await response.json()) as { error?: string };
            if (body.error) {
              errorMessage = body.error;
            }
          } else {
            const responseText = await response.text();
            if (responseText) {
              errorMessage = responseText;
            }
          }
          throw new Error(errorMessage);
        }

        const assistantText = await response.text();
        setMessagesState((current) => [...current, playgroundUiMessage("assistant", assistantText)]);
        return true;
      } catch (caught) {
        const nextError = caught instanceof Error ? caught : new Error(CHAT_REQUEST_FAILED_MESSAGE);
        setError(nextError);
        return true;
      } finally {
        setIsLoading(false);
        isLoadingRef.current = false;
      }
    },
    [api, authedFetch]
  );

  return {
    messages,
    sendMessage,
    setMessages,
    isLoading,
    error
  };
}
